import React, { useEffect, useRef, useState } from 'react';
import Litepicker from 'litepicker';
import { motion } from 'framer-motion';
import { AnimatePresence as FramerAnimatePresence } from 'framer-motion';
import '../styles/spinner.css';

const AnimatePresence = FramerAnimatePresence as React.FC<{
  children: React.ReactNode;
  mode?: "sync" | "wait" | "popLayout";
  initial?: boolean;
}>;

const DiscoveryPage: React.FC = () => {
  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, any[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (calendarContainerRef.current) {
      const picker = new Litepicker({
        element: calendarContainerRef.current,
        singleMode: true,
        inlineMode: true,
        maxDate: new Date(),
        setup: (picker) => {
          picker.on('selected', (startDate: { format: (format: string) => string }) => {
            setSelectedDate(startDate.format('YYYY-MM-DD'));
          });
        }
      });
      return () => picker.destroy();
    }
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    // TO DO:
    // (1) MAKE THE NEXT/PREV MONTH BUTTONS DARK GREY ON HOVER
    // (2) APPLY SAME COLOR TO DATES ON HOVER
    style.textContent = `
      :root {
        --litepicker-is-selected-color-bg: #636363 !important;
        --litepicker-is-start-color-bg: #636363 !important;
        --litepicker-is-end-color-bg: #636363 !important;
        --litepicker-is-selected-color: #fff !important;
        --litepicker-is-start-color: #fff !important;
        --litepicker-is-end-color: #fff !important;
        --litepicker-day-color-hover: #636363 !important;
        --litepicker-button-prev-month-color-hover: #636363 !important;
        --litepicker-button-next-month-color-hover: #636363 !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleQueryClick = async () => {
    if (!selectedDate) {
      alert('Please select a date first.');
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      console.log(`Fetching data for date: ${selectedDate}`);
      const response = await fetch(`/api/discovery_query?date=${selectedDate}`);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${errorText}`);
      }
      console.log('Successfully fetched data');
      const data = await response.json();
      setResults(data);
    } 
    catch (error) {
      console.error('Error fetching data:', error);
      setError('An error occurred while querying the data. Check the logs for more details.');
    }
    finally {
      setIsLoading(false);
    }
  };

  const formatColumnName = (column: string): string => {
    return column
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const renderTable = (tableName: string, data: any[]) => {
    if (data.length === 0) {
      return <p className="mb-6">No data found for {formatColumnName(tableName)}.</p>;
    }

    const linkConfig = tableName.includes('yc_companies')
      ? { column: 'company_name', urlField: 'company_website' }
      : { column: 'full_name', urlField: 'linkedin_url' };

    const columns = Object.keys(data[0]).filter(
      column => column !== linkConfig.urlField
    );

    return (
      <motion.div 
        className="mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <h3 className="text-lg font-bold mb-2">{formatColumnName(tableName)}</h3>
        <motion.table 
          className="table-auto border-collapse border border-gray-400 w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <thead>
            <tr className="bg-gray-200">
              {columns.map((column) => (
                <th key={column} className="border border-gray-400 px-2 py-1 text-left text-sm">
                  {formatColumnName(column)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-100">
                {columns.map((column, colIndex) => (
                  <td key={colIndex} className="border border-gray-400 px-2 py-1 text-xs">
                    {column === linkConfig.column ? (
                      <a
                        href={row[linkConfig.urlField]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-xs"
                      >
                        {row[column]}
                      </a>
                    ) : (
                      row[column] !== null && row[column] !== undefined
                        ? row[column].toString()
                        : ''
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </motion.table>
      </motion.div>
    );
  };

  return (
    <div className="p-4">
      <div className="mb-4">
        <div id="calendar" ref={calendarContainerRef}></div>
      </div>
      <button
        onClick={handleQueryClick}
        disabled={isLoading}
        className={`bg-[#0000EE] text-white px-4 py-2 rounded hover:bg-[#0000CC] disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {isLoading ? 'Querying...' : 'Query'}
      </button>

      <AnimatePresence mode="wait" initial={false}>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mt-6 flex items-center justify-center"
          >
            <div className="mac-spinner"></div>
            <span className="ml-2">Loading results...</span>
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-red-500 mt-4"
        >
          {error}
        </motion.div>
      )}
      
      <AnimatePresence mode="wait" initial={false}>
        {Object.keys(results).length > 0 && (
          <motion.div 
            className="mt-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {Object.entries(results).map(([tableName, data], index) => (
              <motion.div 
                key={tableName}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                {renderTable(tableName, data)}
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DiscoveryPage;
